import { KPI, KPIMonthlyValue } from "../api/fetchMemberDashboard/types";
import { KPIConfig } from "../config/kpiConfig";
import { DashboardKPI, KPIUnit } from "../types";

/** Pairs the API's numbers with the config that formats and explains them. */

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-07` → `Jul`. Parsed rather than dated, to avoid a timezone shifting the month. */
const shortMonth = (yearMonth: string): string => {
  const index = Number(yearMonth.slice(5, 7)) - 1;

  return MONTH_LABELS[index] ?? yearMonth;
};

/** `2026-07` → `Jul 2026`. */
const longMonth = (yearMonth: string): string =>
  `${shortMonth(yearMonth)} ${yearMonth.slice(0, 4)}`;

export const formatValue = (value: number, unit: KPIUnit): string => {
  switch (unit) {
    case "percent":
      return `${value.toFixed(1)}%`;
    case "days":
      return `${Math.round(value)} days`;
    case "ratio":
      return value.toFixed(2);
    case "currency":
    default:
      return formatCurrency(value);
  }
};

const formatCurrency = (value: number): string => {
  const magnitude = Math.abs(value);

  if (magnitude >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }

  if (magnitude >= 10_000) {
    return `$${Math.round(value / 1_000)}K`;
  }

  return `$${Math.round(value).toLocaleString("en-US")}`;
};

/**
 * Money written out in full — "$26,586" rather than "$27K".
 *
 * `formatCurrency` abbreviates above ten thousand, which is right for a
 * headline and wrong for a worked example the member can check. "$27K ÷ 42"
 * comes to 642, not the 633 the card reports, and a denominator small enough
 * to divide in your head is a denominator someone will.
 */
const formatCurrencyExact = (value: number): string =>
  `$${Math.round(value).toLocaleString("en-US")}`;

const formatDelta = (value: number, unit: KPIUnit): string => {
  const magnitude = Math.abs(value);

  switch (unit) {
    case "percent":
      return `${magnitude.toFixed(1)} pts`;
    case "days":
      return `${Math.round(magnitude)} days`;
    case "ratio":
      return magnitude.toFixed(2);
    case "currency":
    default:
      return formatCurrency(magnitude);
  }
};

/**
 * The numerator with the member's own figures substituted in.
 *
 * A margin's numerator is what is *left* of revenue once costs come out, so
 * printing it as a single figure hides where it came from — "$107K" appears
 * nowhere in the member's QuickBooks and matches nothing on the card. Those
 * KPIs show the subtraction their formula describes: "($262K − $155K)".
 *
 * The deduction is derived rather than sent: the numerator is the remainder of
 * the denominator, so what came out is the difference between them.
 */
/**
 * A plain count — job numbers, not money.
 *
 * Kept apart from `formatCurrency` because that abbreviates past ten thousand,
 * which is right for revenue and wrong for a quantity a member can tally.
 */
const formatCount = (value: number): string =>
  Math.round(value).toLocaleString("en-US");

/**
 * The bottom of the fraction, in its own units.
 *
 * Every KPI up to section 03 divided money by money, so this was `formatCurrency`
 * outright. Average Service Ticket divides revenue by a **job count**, which
 * that would render as "$42".
 */
const denominatorFigure = (denominator: number, config: KPIConfig): string => {
  if (config.denominatorUnit !== "count") {
    return formatCurrency(denominator);
  }

  const noun = config.denominatorNoun ? ` ${config.denominatorNoun}` : "";

  return `${formatCount(denominator)}${noun}`;
};

const numeratorFigure = (
  numerator: number,
  denominator: number,
  config: KPIConfig,
): string => {
  if (config.numeratorIsRemainder) {
    return `(${formatCurrency(denominator)} − ${formatCurrency(denominator - numerator)})`;
  }

  // A closing ratio divides jobs by jobs, so the top is not money either.
  if (config.numeratorUnit === "count") {
    return formatCount(numerator);
  }

  // Abbreviation is kept where both halves are money at K or M scale — nobody
  // reconciles "$262K ÷ $819K" by eye, and spelling those out would make ten
  // shipped cards harder to read for no gain.
  return config.denominatorUnit === "count"
    ? formatCurrencyExact(numerator)
    : formatCurrency(numerator);
};

/**
 * The division behind a figure — "$79K ÷ $249K", or "$60K ÷ ($819K ÷ 396)".
 *
 * Days Sales Outstanding and Days Cash on Hand send a **period-total**
 * denominator and scale it by the window's length inside the calculator.
 * Printing `A ÷ B` for those would show an equation that does not close: $60K ÷
 * $819K is 0.07, not the 29 days on the card. So the period is folded back in
 * here, bracketed, exactly where the arithmetic applied it.
 *
 * Every other KPI has no `periodScaling` and reads as it always has.
 */
const division = (
  numerator: number,
  denominator: number,
  config: KPIConfig,
  periodDays: number,
): string => {
  const top = numeratorFigure(numerator, denominator, config);
  const bottom = denominatorFigure(denominator, config);

  switch (config.periodScaling) {
    case "multiplier":
      return `(${top} ÷ ${bottom}) × ${periodDays}`;
    case "denominator":
      return `${top} ÷ (${bottom} ÷ ${periodDays})`;
    default:
      return `${top} ÷ ${bottom}`;
  }
};

/**
 * "$79K ÷ $249K" — one month's own arithmetic, for the trend chart's hover.
 *
 * The result is left off: the tooltip shows it on the line above, so repeating
 * it here would say the same thing twice. A `× 100` is left off for the same
 * reason — but a period scaling is not, because without it the division shown
 * is not the one that produced the figure.
 *
 * Null when the month came from no division at all. A balance sheet KPI is a
 * single figure the tooltip already shows, so there is no working to add.
 */
const working = (
  point: KPIMonthlyValue,
  config: KPIConfig,
  periodDays: number,
): string | null => {
  if (point.numerator === null || point.denominator === null) {
    return null;
  }

  return division(point.numerator, point.denominator, config, periodDays);
};

/**
 * The headline month's own arithmetic, in one of two shapes.
 *
 * A ratio shows its working — "$318 ÷ $1,000 × 100 = 31.8%". A balance has no
 * working to show, so it shows the figure and the month it belongs to instead:
 * "$138K as of Jul 2026". Both answer the same question a member asks of the
 * card back — where did this number come from — and neither invents a division
 * that did not happen.
 *
 * A period-scaled KPI takes its multiplier from `division()`, which prints the
 * number, so `config.multiplier` — the prose the card's fraction shows — is
 * left off rather than printed twice.
 *
 * **Every division joins with `≈`, not `=`** (BS-10, decided 2026-09-17). Each
 * operand is rounded or abbreviated for display, so the printed equation lands
 * near its result rather than on it: `$26,586 ÷ 42` is 632.99, and the card says
 * `$633`. `≈` was already used for the two period-scaled cards and the same
 * argument was always true of the other seven — `=` over operands that do not
 * reproduce the result is a small untruth on every card that rounds. A balance
 * keeps no separator at all, because it shows no division.
 */
const workedExample = (
  kpi: KPI,
  config: KPIConfig,
  monthLabel: string | null,
  periodDays: number,
): string | null => {
  if (kpi.value === null) {
    return null;
  }

  if (kpi.numerator === null || kpi.denominator === null) {
    return monthLabel === null
      ? formatValue(kpi.value, config.unit)
      : `${formatValue(kpi.value, config.unit)} as of ${monthLabel}`;
  }

  const scaled = division(kpi.numerator, kpi.denominator, config, periodDays);
  const result = formatValue(kpi.value, config.unit);

  if (config.periodScaling) {
    return `${scaled} ≈ ${result}`;
  }

  const multiplier = config.multiplier ? ` ${config.multiplier}` : "";

  return `${scaled}${multiplier} ≈ ${result}`;
};

export const toDashboardKPI = (
  kpi: KPI,
  config: KPIConfig,
  periodDays: number,
): DashboardKPI => {
  const delta = kpi.yearOverYearDelta;
  const isUp = (delta ?? 0) > 0;
  const monthLabel = kpi.yearMonth === null ? null : longMonth(kpi.yearMonth);

  return {
    code: kpi.code,
    name: config.name,
    source: config.source,
    definition: config.definition,
    guidance: config.guidance,
    valueLabel: kpi.value === null ? null : formatValue(kpi.value, config.unit),
    monthLabel,
    deltaLabel:
      delta === null || kpi.comparedToYearMonth === null
        ? null
        : `${formatDelta(delta, config.unit)} vs ${longMonth(kpi.comparedToYearMonth)}`,
    deltaIsGood:
      delta === null
        ? true
        : config.direction === "lower_is_better"
          ? delta <= 0
          : delta >= 0,
    deltaIsUp: isUp,
    chart: kpi.monthlyValues.map((point) => ({
      yearMonth: point.yearMonth,
      label: shortMonth(point.yearMonth),
      value: point.value,
      monthLabel: longMonth(point.yearMonth),
      valueLabel:
        point.value === null ? null : formatValue(point.value, config.unit),
      workingLabel:
        point.value === null ? null : working(point, config, periodDays),
    })),
    formula: {
      numerator: config.numeratorLabel,
      denominator: config.denominatorLabel,
      multiplier: config.multiplier,
      workedExample: workedExample(kpi, config, monthLabel, periodDays),
      note: config.note,
    },
    isIncomplete: kpi.isIncomplete,
    incompleteReason: kpi.incompleteReason,
    isAvailable: kpi.isAvailable,
    unavailableReason: kpi.unavailableReason,
    workTypeLabel: config.workTypeLabel ?? null,
    missingAccountLabels: kpi.missingLedgerAccounts.map(
      (ledgerAccount) => ledgerAccount.label,
    ),
  };
};
